import React, { useState, useEffect } from 'react';
import { useLocationStore } from 'context/LocationContext';
import { fetchWeatherDetails } from 'utils/methods';

const Weather = () => {
  const {
    selectedLocationProfile,
    locationProfiles,
    setSelectedLocation,
    addProfile,
    updateProfile,
    deleteProfile: removeProfile,
    fetchCurrentLocationData,
  } = useLocationStore();

  const [selectedLocation, setSelectedLocationState] = useState(selectedLocationProfile);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showNewProfileModal, setShowNewProfileModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTemperature, setEditedTemperature] = useState('');
  const [editedHumidity, setEditedHumidity] = useState('');
  const [newProfile, setNewProfile] = useState({
    name: '',
    location: '',
    temperature: '',
    humidity: '',
    editable: false
  });

  useEffect(() => {
    setSelectedLocationState(selectedLocationProfile);
  }, [selectedLocationProfile]);

  // Add effect to force re-render when location profiles change
  useEffect(() => {
    // This will cause the component to re-render when locationProfiles change
    if (locationProfiles.length > 0 && selectedLocationProfile) {
      // Find and update the selected location from the profiles array
      const updatedSelected = locationProfiles.find(p => p.id === selectedLocationProfile.id);
      if (updatedSelected && JSON.stringify(updatedSelected) !== JSON.stringify(selectedLocationProfile)) {
        setSelectedLocationState(updatedSelected);
      }
    }
  }, [locationProfiles, selectedLocationProfile]);

  // Debug: Log when selected location changes
  // useEffect(() => {
  //   if (selectedLocation) {
  //     console.log("Selected location updated:", selectedLocation);
  //     console.log("Is editable:", selectedLocation.editable || selectedLocation.edit ? "Yes" : "No");
  //   }
  // }, [selectedLocation]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        const savedProfiles = localStorage.getItem('locationProfiles');
        if (!savedProfiles || savedProfiles === '[]') {
          // Initialize with default profile
          await fetchCurrentLocationData();
        }
        
        // Update the current location profile if it exists
        if (locationProfiles.length > 0) {
          const currentLocationProfile = locationProfiles.find(p => p.id === 1);
          if (currentLocationProfile) {
            await refreshWeatherData();
          }
        }
      } catch (error) {
        console.error('Error initializing weather data:', error);
        // Fallback to default data
        const defaultProfile = {
          id: 1,
          name: 'Default Location',
          location: 'Chennai, India',
          temperature: 28,
          humidity: 75,
          editable: false
        };
        setSelectedLocationState(defaultProfile);
      }
    };
    initializeData();
  // eslint-disable-next-line
  }, []);

  const handleLocationChange = async (event) => {
    const value = event.target.value;
    
    if (value === 'add_new') {
      setShowNewProfileModal(true);
    } else if (value) {
      setLoading(true);
      try {
        const profile = JSON.parse(value);
        
        if (profile.id === 1) {
          // Current Location profile (auto-detect)
          const currentLocationData = await fetchCurrentLocationData();
          setSelectedLocationState(currentLocationData);
          setSelectedLocation(currentLocationData);
        } else if (!profile.editable && profile.location) {
          // Fetch fresh weather data for this location
          const weatherData = await fetchWeatherDetails(profile.location);
          const updatedProfile = {
            ...profile,
            location: `${weatherData.location.name}, ${weatherData.location.country}`,
            temperature: Math.round(weatherData.current.temp_c),
            humidity: weatherData.current.humidity
          };
          updateProfile(updatedProfile);
          setSelectedLocationState(updatedProfile);
          setSelectedLocation(updatedProfile);
        } else {
          // Use stored data for editable profiles
          setSelectedLocationState(profile);
          setSelectedLocation(profile);
        }
      } catch (error) {
        console.error('Failed to fetch weather data for selected profile:', error);
        // Fall back to stored data
        const profile = JSON.parse(value);
        setSelectedLocationState(profile);
        setSelectedLocation(profile);
      } finally {
        setLoading(false);
      }
    }
  };

  const saveNewProfile = async () => {
    if (!newProfile.name) {
      alert('Please enter a profile name');
      return;
    }

    if (!newProfile.location) {
      alert('Please enter a location');
      return;
    }

    setLoading(true);
    try {
      let profile;
      
      if (!newProfile.editable) {
        // Try to fetch weather data
        try {
          const weatherData = await fetchWeatherDetails(newProfile.location);
          profile = {
            id: Date.now(),
            name: newProfile.name,
            location: `${weatherData.location.name}, ${weatherData.location.country}`,
            temperature: Math.round(weatherData.current.temp_c),
            humidity: weatherData.current.humidity,
            editable: false
          };
        } catch (error) {
          // If weather fetch fails, create with manual data
          profile = {
            id: Date.now(),
            name: newProfile.name,
            location: newProfile.location || newProfile.name,
            temperature: parseInt(newProfile.temperature),
            humidity: parseInt(newProfile.humidity),
            editable: true
          };
        }
      } else {
        // Use manually entered data
        profile = {
          id: Date.now(),
          name: newProfile.name,
          location: newProfile.location,
          temperature: parseInt(newProfile.temperature),
          humidity: parseInt(newProfile.humidity),
          editable: true
        };
      }

      addProfile(profile);
      setSelectedLocationState(profile);
      setSelectedLocation(profile);
      setShowNewProfileModal(false);
      setNewProfile({ name: '', location: '', temperature: '', humidity: '', editable: false });
    } catch (error) {
      console.error('Error creating new profile:', error);
      alert('Failed to create weather profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteProfile = () => {
    if (selectedLocation && selectedLocation.id !== 1) {
      removeProfile(selectedLocation.id);
      setShowDeleteConfirmModal(false);
      
      // Reset to first available profile
      const defaultProfile = locationProfiles.find(p => p.id === 1) || locationProfiles[0];
      if (defaultProfile) {
        setSelectedLocationState(defaultProfile);
        setSelectedLocation(defaultProfile);
      }
    }
  };

  const handleEditStart = () => {
    console.log("Starting edit with temperature:", selectedLocation.temperature, "humidity:", selectedLocation.humidity);
    
    // Convert numbers to strings for editing
    const tempString = selectedLocation.temperature !== undefined ? 
      String(selectedLocation.temperature) : '';
    const humidityString = selectedLocation.humidity !== undefined ? 
      String(selectedLocation.humidity) : '';
    
    setEditedTemperature(tempString);
    setEditedHumidity(humidityString);
    setIsEditing(true);
  };

  const handleEditSave = () => {
    if (selectedLocation) {
      console.log("Saving edited values:", {
        temperature: editedTemperature,
        humidity: editedHumidity
      });
      
      // Parse values, ensuring we have valid numbers
      const tempValue = editedTemperature.trim() === '' ? 
        selectedLocation.temperature : 
        (isNaN(parseInt(editedTemperature)) ? selectedLocation.temperature : parseInt(editedTemperature));
      
      console.log("Parsed temperature value:", tempValue);
        
      let humidityValue = editedHumidity.trim() === '' ? 
        selectedLocation.humidity : 
        (isNaN(parseInt(editedHumidity.replace(/[^\d]/g, ''))) ? selectedLocation.humidity : parseInt(editedHumidity.replace(/[^\d]/g, '')));
      
      // Ensure humidity is not less than 0
      humidityValue = Math.max(0, humidityValue);
      
      const updatedProfile = {
        ...selectedLocation,
        temperature: tempValue,
        humidity: humidityValue,
        editable: true, // Mark as editable since we're manually setting values
        edit: true      // For UI indication
      };
      
      console.log("Updated profile before saving:", updatedProfile);
      
      // First update in the context
      updateProfile(updatedProfile);
      
      // Then update local state
      setSelectedLocationState(updatedProfile);
      setSelectedLocation(updatedProfile);
      
      // Also ensure it's saved to localStorage for persistence
      const profiles = JSON.parse(localStorage.getItem('locationProfiles') || '[]');
      const updatedProfiles = profiles.map(p => 
        p.id === updatedProfile.id ? updatedProfile : p
      );
      localStorage.setItem('locationProfiles', JSON.stringify(updatedProfiles));
      
      console.log("Profile saved. Current profiles:", updatedProfiles);
      
      setIsEditing(false);
    }
  };

  const handleEditCancel = () => {
    setIsEditing(false);
  };

  const makeProfileEditable = () => {
    if (selectedLocation) {
      console.log("Making profile editable:", selectedLocation);
      
      // Clone the current profile but mark it as editable
      const updatedProfile = {
        ...selectedLocation,
        editable: true, // Mark as manually editable
        edit: true     // Flag for UI indication
      };
      
      // Update in context
      updateProfile(updatedProfile);
      
      // Update in state
      setSelectedLocationState(updatedProfile);
      setSelectedLocation(updatedProfile);
      
      // Update in localStorage
      const profiles = JSON.parse(localStorage.getItem('locationProfiles') || '[]');
      const updatedProfiles = profiles.map(p => 
        p.id === updatedProfile.id ? updatedProfile : p
      );
      localStorage.setItem('locationProfiles', JSON.stringify(updatedProfiles));
      
      // Start editing right away
      setTimeout(() => {
        handleEditStart();
      }, 100);
      
      console.log("Profile made editable:", updatedProfile);
    }
  };

  const refreshWeatherData = async () => {
    if (!selectedLocation || selectedLocation.editable || selectedLocation.edit) return;
    
    setLoading(true);
    try {
      const weatherData = await fetchWeatherDetails(selectedLocation.location);
      const updatedProfile = {
        ...selectedLocation,
        location: `${weatherData.location.name}, ${weatherData.location.country}`,
        temperature: Math.round(weatherData.current.temp_c),
        humidity: weatherData.current.humidity
      };
      updateProfile(updatedProfile);
      setSelectedLocationState(updatedProfile);
      setSelectedLocation(updatedProfile);
    } catch (error) {
      console.error('Failed to refresh weather data:', error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      {/* Weather Widget */}
      <div className="weather-widget">
        <button 
          onClick={() => setShowDetailsModal(true)}
          className="rounded-xl hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-blue-300 p-3 relative"
          disabled={loading}
        >
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-xl">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <i className="fas fa-map-marker-alt text-blue-500 text-sm"></i>
              <div className="text-left">
                <p className="text-xs font-medium text-gray-500 uppercase">Location</p>
                <p className="text-sm font-semibold text-gray-800">
                  {selectedLocation?.location || 'Not Selected'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <i className="fas fa-temperature-high text-orange-500 text-sm"></i>
              <div className="text-left">
                <p className="text-xs font-medium text-gray-500 uppercase">Temp</p>
                <p className="text-sm font-semibold text-gray-800">
                  {selectedLocation?.temperature !== undefined ? selectedLocation.temperature : '--'}°C
                </p>
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{margin: 0}}>
          <div className="bg-white rounded-lg p-6 w-full max-w-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                  <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                </svg>
                Weather Details
              </h2>
              <button onClick={() => setShowDetailsModal(false)} className="text-gray-500 hover:text-gray-700">
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            {/* Location Profile Selector */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
              <div className="flex gap-2">
                <div className="relative flex-grow">
                  <select 
                    value={selectedLocation ? JSON.stringify(selectedLocation) : ''}
                    onChange={handleLocationChange}
                    disabled={loading}
                    className="w-full p-3 pl-4 pr-10 border rounded-lg appearance-none bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 transition-all duration-200 disabled:opacity-50"
                  >
                    <option value="" disabled>Select Location Profile</option>
                    {locationProfiles.map(profile => (
                      <option key={profile.id} value={JSON.stringify(profile)}>
                        {profile.name} {profile.id === 1 && profile.location !== 'Auto Detect' ? `(${profile.location})` : ''}
                      </option>
                    ))}
                    <option value="add_new" className="text-blue-500 font-medium">+ Add New Location</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                    </svg>
                  </div>
                </div>

                {selectedLocation && !selectedLocation.editable && !selectedLocation.edit && (
                  <button 
                    onClick={refreshWeatherData}
                    disabled={loading}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
                    title="Refresh Weather Data"
                  >
                    <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                    </svg>
                    <span className="hidden sm:inline">Refresh</span>
                  </button>
                )}

                {selectedLocation && selectedLocation.id !== 1 && (
                  <button 
                    onClick={() => setShowDeleteConfirmModal(true)}
                    disabled={loading}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
                    title="Delete Profile"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                    </svg>
                    <span className="hidden sm:inline">Delete</span>
                  </button>
                )}
              </div>
            </div>

            {/* Weather Display */}
            {selectedLocation && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                  </svg>
                  Current Conditions
                  {(selectedLocation.editable || selectedLocation.edit) && (
                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">Manual</span>
                  )}
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Temperature Card */}
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-lg border border-orange-100">
                    <div className="flex items-center justify-between flex-col lg:flex-row">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                        </svg>
                        <span className="text-sm font-medium text-gray-600">Temperature</span>
                      </div>
                      {isEditing ? (
                        <input
                          type="text"
                          inputMode="numeric"
                          className="w-20 border rounded p-1 text-xl font-bold text-gray-800"
                          value={editedTemperature}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow empty string, negative sign, or valid numbers
                            if (value === '' || value === '-' || /^-?\d*$/.test(value)) {
                              setEditedTemperature(value);
                            }
                          }}
                        />
                      ) : (
                        <span className="text-2xl font-bold text-gray-800">{selectedLocation.temperature}°C</span>
                      )}
                    </div>
                  </div>

                  {/* Humidity Card */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between flex-col lg:flex-row">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                        </svg>
                        <span className="text-sm font-medium text-gray-600">Humidity</span>
                      </div>
                      {isEditing ? (
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          min="0"
                          className="w-20 border rounded p-1 text-xl font-bold text-gray-800"
                          value={editedHumidity}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only update if it's empty or a positive number
                            if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
                              setEditedHumidity(value);
                            }
                          }}
                        />
                      ) : (
                        <span className="text-2xl font-bold text-gray-800">{selectedLocation.humidity}%</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Last Updated Info */}
                <div className="mt-4 flex justify-between items-center">
                  <div>
                    {isEditing ? (
                      <div className="flex gap-2">
                        <button 
                          onClick={handleEditSave} 
                          className="px-3 py-1 bg-green-500 text-white rounded-md text-sm hover:bg-green-600"
                        >
                          Save
                        </button>
                        <button 
                          onClick={handleEditCancel} 
                          className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        {selectedLocation.editable || selectedLocation.edit ? (
                          <button 
                            onClick={handleEditStart} 
                            className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
                          >
                            Edit Values
                          </button>
                        ) : (
                          <button 
                            onClick={makeProfileEditable} 
                            className="px-3 py-1 bg-yellow-500 text-white rounded-md text-sm hover:bg-yellow-600"
                          >
                            Make Editable
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    Last updated: {new Date().toLocaleString()}
                    {!selectedLocation.editable && !selectedLocation.edit && (
                      <span className="ml-2 text-green-600">• Live data</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => setShowDetailsModal(false)}                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Profile Modal */}
      {showNewProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{margin: 0}}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Create New Profile</h3>
              <button onClick={() => setShowNewProfileModal(false)} className="text-gray-500 hover:text-gray-700">
                <span className="text-3xl">&times;</span>
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <span className="font-medium w-32">Profile Name:</span>
                <input 
                  value={newProfile.name}
                  onChange={(e) => setNewProfile({...newProfile, name: e.target.value})}
                  type="text"
                  placeholder="Enter profile name"
                  className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center gap-4">
                <span className="font-medium w-32">Location:</span>
                <input 
                  value={newProfile.location}
                  onChange={(e) => setNewProfile({...newProfile, location: e.target.value})}
                  type="text"
                  placeholder="Enter location (optional)"
                  className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center gap-4">
                <span className="font-medium w-32">Temperature:</span>
                <input 
                  value={newProfile.temperature}
                  onChange={(e) => setNewProfile({...newProfile, temperature: e.target.value})}
                  type="number"
                  placeholder="Enter temperature"
                  className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">°C</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-medium w-32">Humidity:</span>
                <input 
                  value={newProfile.humidity}
                  onChange={(e) => setNewProfile({...newProfile, humidity: e.target.value})}
                  type="number"
                  placeholder="Enter humidity"
                  className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">%</span>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newProfile.editable}
                    onChange={(e) => setNewProfile({...newProfile, editable: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm">Manual data entry (disable auto-fetch)</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => setShowNewProfileModal(false)} 
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                onClick={saveNewProfile} 
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Profile'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{margin: 0}}>
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the profile "{selectedLocation?.name}"?
            </p>
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setShowDeleteConfirmModal(false)} 
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDeleteProfile} 
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Weather;
